import Fieldset from '@/components/displays/Fieldset';
import { capture } from '@/handlers/error';
import { AudioAssessmentResult, isAdjustedAudioAssessmentResult } from '@/types/pronouciation';
import { Question, isSlideComponentAudioRecord, isSlideComponentCharacter, isSlideComponentPronunciationAssessment } from '@/types/slide';
import InsightsIcon from '@mui/icons-material/Insights';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioAssessmentOverallScore } from './components/AudioAssessmentOverallScore';
import { SentenceTable } from './components/SentenceTable';
import { WordRatingTable } from './components/WordRatingTable';

const ANIMATION_DURATION = 500; // 0.5 second

export const AudioAssessment = ({ question }: { question?: Question; src: string }) => {
    const { t } = useTranslation();

    const [adjustedResult, setAdjustedResult] = useState<AudioAssessmentResult | undefined>(undefined);

    useEffect(() => {
        if (question && question.answers.length > 0) {
            // get the latest answer
            const answer = question.answers[0];
            if (answer?.data) {
                try {
                    const parsedAnswer = JSON.parse(answer.data);
                    if (!isAdjustedAudioAssessmentResult(parsedAnswer.result)) {
                        capture(new Error('Invalid audio assessment result'));
                    }
                    setAdjustedResult(parsedAnswer.result);
                } catch (e) {
                    capture(e);
                }
            }
        }
    }, [question]);

    const defaultRefText = useMemo(() => {
        if (!question || !question.content) return '';

        if (isSlideComponentCharacter(question)) {
            return question.content.phrase;
        }

        if (isSlideComponentPronunciationAssessment(question)) {
            return question.content?.refText;
        }

        if (isSlideComponentAudioRecord(question)) {
            return '';
        }

        capture(new Error('Error determining default ref text for audio assessment'));
        return '';
    }, [question]);

    const { feedback, sentences } = useMemo(() => {
        let feedbackText = '';
        const sentences: AudioAssessmentResult['characters'][] = [];
        if (adjustedResult && defaultRefText) {
            let idx = 0;
            let sentence: AudioAssessmentResult['characters'] = [];
            for (const character of defaultRefText) {
                if (adjustedResult.characters && adjustedResult.characters.length > idx && character === adjustedResult.characters[idx]?.text) {
                    const word = adjustedResult.characters[idx];
                    if (word !== undefined && word !== null) {
                        sentence.push(word);
                        if (!word.pass) {
                            feedbackText += `<span style="color: #EF5350">${character}</span>`;
                        } else {
                            feedbackText += character;
                        }
                    }
                    idx++;
                } else {
                    if (sentence.length > 0) {
                        const segment = [...sentence];
                        sentences.push(segment);
                        sentence = [];
                    }
                    feedbackText += character;
                }
            }
            if (sentence.length > 0) {
                const segment = [...sentence];
                sentences.push(segment);
            }
        }
        return { feedback: feedbackText, sentences };
    }, [adjustedResult, defaultRefText]);

    return (
        <Box
            sx={(theme) => ({
                visibility: 'visible',
                display: 'flex',
                height: '100%',
                width: 540,
                opacity: 1,
                transition: 'width visibility opacity ease-in-out',
                transitionDuration: `${ANIMATION_DURATION}ms`,
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
                overflow: 'hidden',
                backgroundColor: (theme.vars || theme).palette.grey[100],
                ...theme.applyStyles('dark', {
                    backgroundColor: (theme.vars || theme).palette.grey[800],
                }),
            })}
        >
            <Divider orientation='vertical' />
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        p: 1,
                        gap: 1,
                    }}
                >
                    <Fieldset title={`${t('assessment.audio.title')} (${t('common.putonghua')})`} sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
                            <Paper sx={{ display: 'flex', p: 1, gap: 1, alignItems: 'center' }}>
                                <Typography
                                    sx={{
                                        flex: 1,
                                        p: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        overflowWrap: 'break-word',
                                    }}
                                >
                                    <span dangerouslySetInnerHTML={{ __html: feedback }} />
                                </Typography>
                            </Paper>
                            <Paper sx={{ p: 1, flexGrow: 1 }}>
                                {adjustedResult ? (
                                    <Paper sx={{ maxHeight: 380, overflowY: 'auto' }}>
                                        <AudioAssessmentOverallScore
                                            overall={adjustedResult?.calculatedScore?.overall ?? -1}
                                            pronunciation={adjustedResult?.calculatedScore?.pronunciation ?? -1}
                                            initial={adjustedResult?.calculatedScore?.initial ?? -1}
                                            tone={adjustedResult?.calculatedScore?.tone ?? -1}
                                            vowel={adjustedResult?.calculatedScore?.vowel ?? -1}
                                            fluency={adjustedResult?.calculatedScore?.fluency ?? -1}
                                        />
                                        <Divider sx={{ my: 1 }} />
                                        {defaultRefText && defaultRefText.length > 50 ? (
                                            <Box sx={{ mb: 2 }}>
                                                <SentenceTable sentences={sentences} />
                                            </Box>
                                        ) : (
                                            <WordRatingTable words={adjustedResult?.characters ?? []} />
                                        )}
                                    </Paper>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            minWidth: 216,
                                            mx: 4,
                                            gap: 2,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <InsightsIcon sx={{ fontSize: 72, color: 'grey.300' }} />
                                        <Typography color='text.secondary' sx={{ textAlign: 'center' }}>
                                            {t('assessment.audio.label.tips')}
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    </Fieldset>
                </Box>
            </Box>
        </Box>
    );
};
